import React from 'react';
import {
  Banner,
  Button,
  InlineStack,
  Text,
} from '@shopify/polaris';
import { useNavigate } from 'react-router-dom';

interface UpgradePromptProps {
  type: 'warning' | 'limit_reached' | 'trial_ending' | 'expired';
  used?: number;
  limit?: number;
  daysRemaining?: number;
  onDismiss?: () => void;
}

export const UpgradePrompt: React.FC<UpgradePromptProps> = ({
  type,
  used,
  limit,
  daysRemaining,
  onDismiss,
}) => {
  const navigate = useNavigate();

  const handleUpgrade = () => {
    navigate('/pricing');
  };

  const renderContent = () => {
    switch (type) {
      case 'warning':
        return {
          title: 'Approaching Order Limit',
          message: `You've used ${used?.toLocaleString()} of ${limit?.toLocaleString()} orders this billing period. Upgrade to avoid sync interruptions.`,
          tone: 'warning' as const,
          action: 'View Plans',
        };

      case 'limit_reached':
        return {
          title: 'Order Limit Reached',
          message: 'You have reached your monthly order sync limit. Upgrade your plan to continue syncing orders to Tally.',
          tone: 'critical' as const,
          action: 'Upgrade Now',
        };

      case 'trial_ending':
        return {
          title: 'Trial Ending Soon',
          message: `Your free trial ends in ${daysRemaining} day${daysRemaining === 1 ? '' : 's'}. Subscribe now to continue using all features.`,
          tone: 'warning' as const,
          action: 'Subscribe Now',
        };

      case 'expired':
        return {
          title: 'Subscription Expired',
          message: 'Your subscription has expired. Renew to continue syncing orders to Tally.',
          tone: 'critical' as const,
          action: 'Renew Subscription',
        };

      default:
        return null;
    }
  };

  const content = renderContent();
  if (!content) return null;

  return (
    <Banner
      title={content.title}
      tone={content.tone}
      onDismiss={onDismiss}
    >
      <InlineStack gap="400" align="space-between" blockAlign="center">
        <Text as="p">{content.message}</Text>
        <Button onClick={handleUpgrade}>{content.action}</Button>
      </InlineStack>
    </Banner>
  );
};

export default UpgradePrompt;
